import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useApp } from '../context/AppContext';

const SingleJokePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showToast } = useApp();
    const [joke, setJoke] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedJokes, setRelatedJokes] = useState([]);

    useEffect(() => {
        fetchJoke();
        window.scrollTo(0, 0);
    }, [id]);

    const fetchJoke = async () => {
        setLoading(true);
        try {
            // ✅ Fetch from ARTICLES API (since jokes are now articles)
            const res = await api.get(`/articles/${id}`);
            setJoke(res.data);

            // Fetch related jokes (other articles with category Jokes)
            const relatedRes = await api.get(`/articles?category=Jokes&status=live&limit=3`);
            const filtered = relatedRes.data.filter(j => j._id !== id);
            setRelatedJokes(filtered.slice(0, 3));

        } catch (error) {
            console.error('Error fetching joke:', error);
            showToast('Joke not found', 'error');
            navigate('/jokes');
        } finally {
            setLoading(false);
        }
    };

    const handleShare = async () => {
        const shareUrl = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: joke.title,
                    text: joke.excerpt || joke.content?.replace(/<[^>]*>/g, '').substring(0, 200),
                    url: shareUrl,
                });
            } else {
                await navigator.clipboard.writeText(shareUrl);
                showToast('📋 Joke link copied to clipboard!');
            }
        } catch (error) {
            console.error('Error sharing:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Recently';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const stripHtml = (html) => {
        if (!html) return '';
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    };

    if (loading) {
        return (
            <div className="loader" style={{ minHeight: '60vh' }}>
                <div className="loader-spinner"></div>
            </div>
        );
    }

    if (!joke) {
        return (
            <div className="page" style={{ textAlign: 'center', padding: '60px 20px' }}>
                <h2>😂 Joke Not Found</h2>
                <p>The joke you're looking for doesn't exist or has been removed.</p>
                <button className="btn btn-red" onClick={() => navigate('/jokes')} style={{ marginTop: '20px' }}>
                    Browse Jokes →
                </button>
            </div>
        );
    }

    return (
        <div className="page" style={{ paddingTop: '20px', maxWidth: '900px', margin: '0 auto' }}>
            {/* Back Button */}
            <button
                onClick={() => navigate('/jokes')}
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'var(--navy)',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                }}
            >
                ← Back to Jokes
            </button>

            {/* Joke Card */}
            <div style={{
                background: '#fff',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                marginBottom: '30px'
            }}>
                {/* Image */}
                {(joke.img || joke.imgData) && (
                    <div style={{ height: '400px', overflow: 'hidden' }}>
                        <img
                            src={joke.img || joke.imgData}
                            alt={joke.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </div>
                )}

                {/* Content */}
                <div style={{ padding: '30px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <span className="tag" style={{ fontSize: '14px', padding: '4px 12px' }}>
                            {joke.category}
                        </span>
                        <span style={{ fontSize: '13px', color: '#888' }}>
                            📅 {formatDate(joke.date || joke.createdAt)}
                        </span>
                    </div>

                    <h1 style={{
                        fontSize: '28px',
                        lineHeight: '1.4',
                        marginBottom: '20px',
                        color: 'var(--navy)'
                    }}>
                        {joke.title}
                    </h1>

                    {/* Joke Content */}
                    <div style={{
                        background: '#f8f9fa',
                        padding: '30px',
                        borderRadius: '12px',
                        marginBottom: '25px',
                        textAlign: 'center'
                    }}>
                        {joke.content && joke.content.includes('joke-collection') ? (
                            // If it's a joke collection, render the HTML directly
                            <div dangerouslySetInnerHTML={{ __html: joke.content }} />
                        ) : (
                            <p style={{
                                fontSize: '20px',
                                lineHeight: '1.8',
                                color: '#333',
                                fontStyle: 'italic',
                                whiteSpace: 'pre-wrap'
                            }}>
                                “ {stripHtml(joke.content || joke.excerpt || joke.text)} ”
                            </p>
                        )}
                    </div>

                    {/* Stats and Actions */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '15px',
                        paddingTop: '20px',
                        borderTop: '1px solid #eee'
                    }}>
                        <div style={{ display: 'flex', gap: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span>👁️</span>
                                <span>{joke.views || 0} views</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span>❤️</span>
                                <span>{joke.analytics?.totalShares || joke.likes || 0} likes</span>
                            </div>
                        </div>

                        <button
                            onClick={handleShare}
                            style={{
                                padding: '10px 24px',
                                background: '#4ecdc4',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '30px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600'
                            }}
                        >
                            📤 Share this Joke
                        </button>
                    </div>
                </div>
            </div>

            {/* Related Jokes */}
            {relatedJokes.length > 0 && (
                <div style={{ marginTop: '40px' }}>
                    <h2 className="section-title">😂 More Jokes You Might Like</h2>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '20px',
                        marginTop: '20px'
                    }}>
                        {relatedJokes.map(related => (
                            <div
                                key={related._id}
                                onClick={() => navigate(`/article/${related._id}`)}
                                style={{
                                    background: '#fff',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: '1px solid #eee'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div style={{ fontSize: '30px', marginBottom: '10px' }}>😂</div>
                                <h4 style={{
                                    fontSize: '16px',
                                    marginBottom: '8px',
                                    color: 'var(--navy)',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}>
                                    {related.title}
                                </h4>
                                <p style={{ fontSize: '13px', lineHeight: '1.5', color: '#555' }}>
                                    {related.excerpt || stripHtml(related.content || '').substring(0, 80)}...
                                </p>
                                <div style={{ marginTop: '10px', fontSize: '12px', color: '#888' }}>
                                    👁️ {related.views || 0} views
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SingleJokePage;